/**
 * Per-segment `top_slices` → per-slice marks (pure).
 *
 * A mask-localized CADe device emits ONE `top_slices` entry per 26-connected
 * segment, ranked by descending voxel count, so element 0 is the largest. The
 * list REPEATS a UID when several segments sit on the same slice.
 *
 * Grouping keeps both facts: how many slices to step through, and how many
 * segments each one carries. De-duplicating without counting loses the burden;
 * stepping per segment makes most presses land on the slice already displayed.
 *
 * First-appearance order is preserved deliberately — it IS the engine's
 * largest-first ranking, so a reader stepping forward sees the most
 * conspicuous segments first and can stop whenever they like. That is also why
 * nothing here caps or truncates the list.
 */

export interface SegmentationSliceGroup {
  /** SOPInstanceUID of the slice, `.dcm` suffix removed. */
  uid: string;
  /** How many distinct segments the device localized to this slice. */
  segments: number;
}

/** The device may or may not suffix a slice UID with ".dcm". Normalize once. */
export function normalizeSliceUid(uid: string): string {
  return uid.endsWith(".dcm") ? uid.slice(0, -4) : uid;
}

/**
 * Group a finding's `top_slices` into one entry per distinct slice, counting
 * repeats, in first-appearance (largest-first) order.
 */
export function groupSegmentationSlices(topSlices: readonly string[]): SegmentationSliceGroup[] {
  // A Map preserves insertion order, so first-appearance order (the engine's
  // largest-first ranking) falls out of iterating `counts` directly — no
  // separate `order` array needed.
  const counts = new Map<string, number>();
  for (const raw of topSlices) {
    if (!raw) continue;
    const uid = normalizeSliceUid(raw);
    counts.set(uid, (counts.get(uid) ?? 0) + 1);
  }
  return [...counts].map(([uid, segments]) => ({ uid, segments }));
}

export interface SegmentationStripState {
  /** 1-based position in largest-first order, or null when off-mark. */
  position: number | null;
  total: number;
  nextIndex: number;
  prevIndex: number;
}

/**
 * Stepper state for a series' marks. `sliceIndices` must be in largest-first
 * order (as produced by groupSegmentationSlices), NOT sorted by slice.
 *
 * Off-mark, forward steps to the largest segment and backward to the smallest,
 * so a press always does something predictable. On-mark it wraps.
 */
export function segmentationStripState(
  sliceIndices: readonly number[],
  currentSliceIndex: number,
): SegmentationStripState {
  const total = sliceIndices.length;
  if (total === 0) {
    return { position: null, total: 0, nextIndex: -1, prevIndex: -1 };
  }
  const at = sliceIndices.indexOf(currentSliceIndex);
  if (at === -1) {
    return {
      position: null,
      total,
      nextIndex: sliceIndices[0],
      prevIndex: sliceIndices[total - 1],
    };
  }
  return {
    position: at + 1,
    total,
    nextIndex: sliceIndices[(at + 1) % total],
    prevIndex: sliceIndices[(at - 1 + total) % total],
  };
}
